import { deDE, frFR } from '@clerk/localizations';
import type { Locale } from 'date-fns';
import { de, enUS, frCH } from 'date-fns/locale';
import { resources } from './resources/resources.ts';

/**
 * Glossary: a "language" is the bare tag the user chooses ('de'). It selects
 * translations. A "locale" is the full BCP 47 tag ('de-DE') the app formats
 * with. Locales are always derived here, never stored.
 */

export type SupportedLanguage = keyof typeof resources;

export const defaultLanguage = 'de';

export const isSupportedLanguage = (value: string): value is SupportedLanguage => value in resources;

/**
 * Clamps a runtime language tag to the supported set (unknown → default).
 * Strips the region subtag first: `i18n.language` carries the full detected
 * tag ('en-GB'), not the resolved bare language.
 */
export const toSupportedLanguage = (value: string): SupportedLanguage => {
    const language = value.split('-')[0] ?? value;
    return isSupportedLanguage(language) ? language : defaultLanguage;
};

// filter is to avoid ts linting warnings about narrowing string to SupportedLanguage
export const supportedLanguages = Object.keys(resources).filter(isSupportedLanguage);

type LanguageConfig = {
    /** BCP 47 locale for all Intl-based formatting */
    formattingLocale: string;
    /** Locale object for date-fns components (calendar, date picker) */
    dateFnsLocalization: Locale;
    /** Clerk UI translation; undefined uses the Clerk built-in English */
    clerkLocalization: typeof deDE | undefined;
    /** English language name, interpolated into AI prompts as {{outputLanguage}} */
    aiOutputLanguageName: string;
    /** The language's own name, for the language picker */
    nativeName: string;
};

/**
 * Every per-language decision in one place. Registering a new language in
 * `resources` does not compile until it gets a complete entry here.
 */
export const languageConfigs = {
    de: {
        formattingLocale: 'de-DE',
        dateFnsLocalization: de,
        clerkLocalization: deDE,
        aiOutputLanguageName: 'German',
        nativeName: 'Deutsch',
    },
    en: {
        formattingLocale: 'en-US',
        dateFnsLocalization: enUS,
        clerkLocalization: undefined,
        aiOutputLanguageName: 'English',
        nativeName: 'English',
    },
    fr: {
        // Swiss French: dot currency decimals and dotted dates, unlike fr-FR
        formattingLocale: 'fr-CH',
        dateFnsLocalization: frCH,
        // Clerk ships no fr-CH; its French UI copy is region-neutral
        clerkLocalization: frFR,
        aiOutputLanguageName: 'French',
        nativeName: 'Français',
    },
} as const satisfies Record<SupportedLanguage, LanguageConfig>;

/**
 * Languages riding the settings-menu flag (`enableSettingsMenu`). They behave
 * like any other language wherever the flag is on or unknown; only a legacy
 * URL on a tenant with the flag known to be off ignores them.
 */
export const flaggedLanguages: SupportedLanguage[] = ['fr'];

export type SupportedLocale = (typeof languageConfigs)[SupportedLanguage]['formattingLocale'];

/**
 * Resolves the translation language to the BCP 47 locale used for all
 * Intl-based formatting. Unknown languages resolve to the default language.
 */
export function getFormattingLocale(language: string): SupportedLocale {
    return languageConfigs[toSupportedLanguage(language)].formattingLocale;
}
